import { useEffect, type ReactElement } from 'react';

import { DatePicker, Form, Input, Modal, Select } from 'antd';
import dayjs from 'dayjs';

import { useMembers } from '../../tasks/hooks/useTaskLookups.js';
import { memberName } from '../lib/projectMeta.js';
import { getProjectStatusOptions } from '../lib/projectMeta.js';
import {
  useCreateProjectMutation,
  useSetProjectMembersMutation,
  useUpdateProjectMutation,
} from '../hooks/useProjectMutations.js';
import type { ProjectItem, ProjectStatus } from '../api/projects.client.js';
import { i18n } from '../../../i18n/index.js';

interface ProjectFormValues {
  name: string;
  description?: string;
  status: ProjectStatus;
  ownerMemberId?: string;
  startDate?: dayjs.Dayjs;
  dueDate?: dayjs.Dayjs;
  memberIds?: string[];
}

interface ProjectFormModalProps {
  open: boolean;
  onClose: () => void;
  project?: ProjectItem | null;
}

function toValues(project: ProjectItem | null | undefined): ProjectFormValues {
  if (!project) {
    return { name: '', status: 'planned' };
  }
  return {
    name: project.name,
    ...(project.description ? { description: project.description } : {}),
    status: project.status,
    ...(project.ownerMember ? { ownerMemberId: project.ownerMember.id } : {}),
    ...(project.startDate ? { startDate: dayjs(project.startDate) } : {}),
    ...(project.dueDate ? { dueDate: dayjs(project.dueDate) } : {}),
    memberIds: project.members.map((entry) => entry.member.id),
  };
}

export function ProjectFormModal({ open, onClose, project }: ProjectFormModalProps): ReactElement {
  const [form] = Form.useForm<ProjectFormValues>();
  const createMut = useCreateProjectMutation();
  const updateMut = useUpdateProjectMutation();
  const setMembersMut = useSetProjectMembersMutation();
  const membersQuery = useMembers();
  const isEdit = Boolean(project);
  const mutation = isEdit ? updateMut : createMut;
  const isSaving = mutation.isPending || setMembersMut.isPending;

  useEffect(() => {
    if (open) {
      createMut.reset();
      updateMut.reset();
      setMembersMut.reset();
      form.setFieldsValue(toValues(project ?? null));
    }
  }, [open, project, form]);

  const memberOptions = (membersQuery.data ?? []).map((member) => ({
    value: member.id,
    label: memberName(member),
  }));

  const submit = async (): Promise<void> => {
    const values = await form.validateFields();
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || null,
      status: values.status,
      ownerMemberId: values.ownerMemberId ?? null,
      startDate: values.startDate ? values.startDate.toISOString() : null,
      dueDate: values.dueDate ? values.dueDate.toISOString() : null,
    };
    if (isEdit && project) {
      await updateMut.mutateAsync({ id: project.id, input: payload });
      // Member changes ride a dedicated endpoint: without this call the edit
      // saved everything except the participants, silently.
      const nextMemberIds = (values.memberIds ?? []).slice().sort();
      const currentMemberIds = project.members
        .map((entry) => entry.member.id)
        .slice()
        .sort();
      if (nextMemberIds.join(',') !== currentMemberIds.join(',')) {
        await setMembersMut.mutateAsync({ id: project.id, memberIds: values.memberIds ?? [] });
      }
    } else {
      await createMut.mutateAsync({ ...payload, memberIds: values.memberIds ?? [] });
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => void submit()}
      okText={
        isEdit
          ? i18n.t('crm.features.features.projects.components.projectformmodal.сохранить')
          : i18n.t('crm.features.features.projects.components.projectformmodal.создать_проект')
      }
      cancelText={i18n.t('crm.common.cancel')}
      confirmLoading={isSaving}
      destroyOnHidden
      title={
        isEdit
          ? i18n.t(
              'crm.features.features.projects.components.projectformmodal.редактировать_проект',
            )
          : i18n.t('crm.features.features.projects.components.projectformmodal.новый_проект')
      }
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="name"
          label={i18n.t('crm.features.features.projects.components.projectformmodal.название')}
          rules={[
            {
              required: true,
              message: i18n.t(
                'crm.features.features.projects.components.projectformmodal.название_обязательно',
              ),
            },
          ]}
        >
          <Input maxLength={200} data-testid="project-form-name" />
        </Form.Item>
        <Form.Item
          name="status"
          label={i18n.t('crm.features.features.projects.components.projectformmodal.статус')}
        >
          <Select options={getProjectStatusOptions()} data-testid="project-form-status" />
        </Form.Item>
        <Form.Item
          name="ownerMemberId"
          label={i18n.t('crm.features.features.projects.components.projectformmodal.владелец')}
        >
          <Select
            allowClear
            options={memberOptions}
            loading={membersQuery.isLoading}
            data-testid="project-form-owner"
          />
        </Form.Item>
        <Form.Item
          name="memberIds"
          label={i18n.t('crm.features.features.projects.components.projectformmodal.участники')}
        >
          <Select
            mode="multiple"
            options={memberOptions}
            loading={membersQuery.isLoading}
            data-testid="project-form-members"
          />
        </Form.Item>
        <Form.Item
          name="description"
          label={i18n.t('crm.features.features.projects.components.projectformmodal.описание')}
        >
          <Input.TextArea rows={3} maxLength={10_000} data-testid="project-form-description" />
        </Form.Item>
        <div className="flex gap-4">
          <Form.Item
            name="startDate"
            label={i18n.t('crm.features.features.projects.components.projectformmodal.начало')}
          >
            <DatePicker style={{ width: '100%' }} data-testid="project-form-start" />
          </Form.Item>
          <Form.Item
            name="dueDate"
            label={i18n.t('crm.features.features.projects.components.projectformmodal.срок')}
          >
            <DatePicker style={{ width: '100%' }} data-testid="project-form-due" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
